import React, {useEffect, useState} from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker/dist';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {DateTime} from 'luxon';
import moment, {Moment} from 'moment';
import axios from 'axios';
import i18n from 'i18n';
import {useStoreActions} from 'store';
import {Icon} from '@/components/icons/Icon';
import {useDateDependentDataRefresh} from 'utils/useDateDependentDataRefresh';
import {usePeriodConfig} from 'hooks/useConfig';
import {getDefaultDateRange, PeriodDefaultLength} from 'utils/dateUtils';
import {useAlert} from '@/components/alerts';
import {extractErrorMessage} from '@/utils/errorHandling';

interface Props {
    comparableType?: boolean;
}

const HeaderDatePickerComponent = ({
    comparableType = false,
}: Props) => {
    const {showError} = useAlert();
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const updateUserData = useStoreActions((actions) => actions.userData.setData);
    const refreshDateDependentData = useDateDependentDataRefresh();
    const {periodDefaultLength, isLoaded: configLoaded} = usePeriodConfig();


    const [dateRangeStart, setDateRangeStart] = useState<string>('');
    const [dateRangeEnd, setDateRangeEnd] = useState<string>('');

    const [dateCompRangeStart, setDateCompRangeStart] = useState<string>('');
    const [dateCompRangeEnd, setDateCompRangeEnd] = useState<string>('');

    useEffect(() => {

        if (!loadingUserData && configLoaded && dateRangeStart === '' && dateRangeEnd === '') {

            // If user data has session dates, use them
            if (userData?.sessionStartDate && userData?.sessionEndDate) {
                setDateRangeStart(userData.sessionStartDate);
                setDateRangeEnd(userData.sessionEndDate);
                setDateCompRangeStart(userData?.sessionStartCompDate || '');
                setDateCompRangeEnd(userData?.sessionEndCompDate || '');
            } else {
                // Otherwise, use configuration-based defaults
                const safePeriodLength = periodDefaultLength || 'this_month';
                const defaultRange = getDefaultDateRange(safePeriodLength as PeriodDefaultLength);
                setDateRangeStart(defaultRange.startDate);
                setDateRangeEnd(defaultRange.endDate);

                // Set comparison dates to the same defaults initially
                setDateCompRangeStart(defaultRange.startDate);
                setDateCompRangeEnd(defaultRange.endDate);
            }
        } else {
        }
    }, [
        loadingUserData,
        configLoaded,
        periodDefaultLength,
        userData?.sessionStartDate,
        userData?.sessionEndDate,
        userData?.sessionStartCompDate,
        userData?.sessionEndCompDate,
        dateRangeStart,
        dateRangeEnd
    ]);

    // Single early return after all hooks
    if (loadingUserData || !configLoaded || !userData) {
        const reason = loadingUserData ? 'loading user data' :
            !configLoaded ? 'config not loaded' :
                'no user data';
        return <div>{i18n.t('loading_date_picker.text')}</div>;
    }


    const precedingMatchDayOfTheWeekPeriodStartDate = moment(
        userData.precedingMatchDayOfTheWeekPeriodStartDate,
        'DD/MM/YYYY'
    );
    const precedingMatchDayOfTheWeekPeriodEndDate = moment(
        userData.precedingMatchDayOfTheWeekPeriodEndDate,
        'DD/MM/YYYY'
    );

    const precedingPeriodDelta = moment(userData.sessionStartDate, 'DD/MM/YYYY')
        .diff(moment(userData.sessionEndDate, 'DD/MM/YYYY'), 'days');
    const precedingPeriodEndDate = moment(userData.sessionStartDate, 'DD/MM/YYYY')
        .subtract(1, 'days');
    const precedingPeriodStartDate = moment(userData.sessionStartDate, 'DD/MM/YYYY')
        .subtract(Math.abs(precedingPeriodDelta) + 1, 'days');

    const validateDateRange = (startDate: string, endDate: string): string | null => {
        const start = DateTime.fromFormat(startDate, 'dd/MM/yyyy');
        const end = DateTime.fromFormat(endDate, 'dd/MM/yyyy');
        const today = DateTime.now();

        if (!start.isValid || !end.isValid) {
            return i18n.t('jt_invalid_date_format.text', 'Invalid date format');
        }
        if (start > end) {
            return i18n.t('jt_start_date_cannot_be_after_end_date.text', 'Start date cannot be after end date');
        }
        if (start > today || end > today) {
            return i18n.t('jt_dates_cannot_be_in_the_future.text', 'Dates cannot be in the future');
        }
        return null;
    };

    const onPickerApply = async (event: any, picker: any): Promise<void> => {
        if (isSubmitting) {return;}

        // Get the values directly from the picker
        const startDateStr = picker.startDate.toDate().toLocaleDateString('en-GB');
        const endDateStr = picker.endDate.toDate().toLocaleDateString('en-GB');
        const labelStr = picker.chosenLabel || `${startDateStr} - ${endDateStr}`;


        // Update state with the actual picker values
        setDateRangeStart(startDateStr);
        setDateRangeEnd(endDateStr);

        const validationError = validateDateRange(startDateStr, endDateStr);
        if (validationError) {
            showError(validationError);
            return;
        }

        setIsSubmitting(true);

        try {
            const requestData = {
                report_range: `${startDateStr} - ${endDateStr}`,
                redirect_url: window.location.href,
                report_range_string: labelStr
            };

            const response = await axios.post(window.URLS.changeSessionDatesView, requestData, {
                headers: {'Content-Type': 'application/json'},
            });

            if (response.data.success) {
                const updatedUserData = {
                    ...userData,
                    ...response.data.sessionData
                };
                updateUserData(updatedUserData);

                requestAnimationFrame(() => {
                    refreshDateDependentData();
                });
            } else {
                showError(response.data.error || i18n.t('jt_failed_to_update_date_range.text'));
            }
        } catch (error) {
            console.error('Error updating date range:', error);
            const errorMessage = extractErrorMessage(error, i18n.t('jt_failed_to_update_date_range.text'));
            showError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onPickerCompApply = async (event: any, picker: any): Promise<void> => {
        if (isSubmitting) {return;}

        // Get the values directly from the picker
        const startDateStr = picker.startDate.toDate().toLocaleDateString('en-GB');
        const endDateStr = picker.endDate.toDate().toLocaleDateString('en-GB');
        const labelStr = picker.chosenLabel || `${startDateStr} - ${endDateStr}`;


        // Update state with the actual picker values
        setDateCompRangeStart(startDateStr);
        setDateCompRangeEnd(endDateStr);

        const validationError = validateDateRange(startDateStr, endDateStr);
        if (validationError) {
            showError(validationError);
            return;
        }

        setIsSubmitting(true);

        try {
            const requestDataComp = {
                report_range_comp: `${startDateStr} - ${endDateStr}`,
                redirect_url: window.location.href,
                report_range_string_comp: labelStr
            };

            const response = await axios.post(window.URLS.changeSessionDatesView, requestDataComp, {
                headers: {'Content-Type': 'application/json'},
            });

            if (response.data.success) {
                const updatedUserData = {
                    ...userData,
                    ...response.data.sessionData
                };
                updateUserData(updatedUserData);

                requestAnimationFrame(() => {
                    refreshDateDependentData();
                });
            } else {
                showError(response.data.error || i18n.t('jt_failed_to_update_comparison_date_range.text'));
            }
        } catch (error) {
            console.error('Error updating comparison date range:', error);
            const errorMessage = extractErrorMessage(error, i18n.t('jt_failed_to_update_comparison_date_range.text'));
            showError(errorMessage);
            console.error('Error updating comparison date range:', error);
            showError(i18n.t('jt_failed_to_update_comparison_date_range.text'));
        } finally {
            setIsSubmitting(false);
        }
    };
    const precedingMatchLabel = i18n.t('date_preceding_match.text');
    const precedingLabel = i18n.t('date_preceding.text');

    if (comparableType) {
        return (
            <DateRangePicker
                initialSettings={{
                    startDate: dateCompRangeStart || userData?.sessionStartCompDate || '',
                    endDate: dateCompRangeEnd || userData?.sessionEndCompDate || '',
                    locale: {
                        format: 'DD/MM/YYYY',
                        firstDay: 1,
                        customRangeLabel: i18n.t('date_custom.text'),
                    },
                    showWeekNumbers: true,
                    opens: 'left',
                    alwaysShowCalendars: true,
                    maxDate: moment().format('DD/MM/YYYY'),
                    ranges: {
                        [precedingMatchLabel]: [
                            precedingMatchDayOfTheWeekPeriodStartDate,
                            precedingMatchDayOfTheWeekPeriodEndDate
                        ],
                        [precedingLabel]: [precedingPeriodStartDate, precedingPeriodEndDate],
                    },
                }}
                onCallback={(start: Moment, end: Moment, _label) => {
                    const startDateStr = start.toDate().toLocaleDateString('en-GB');
                    const endDateStr = end.toDate().toLocaleDateString('en-GB');

                    setDateCompRangeStart(startDateStr);
                    setDateCompRangeEnd(endDateStr);
                }}
                onApply={onPickerCompApply}
            >
                <h6 id="date-range-picker-compared" className="text-muted">
                    {i18n.t('date_compared.text')} {dateCompRangeStart}-{dateCompRangeEnd}
                    {isSubmitting ? (
                        <Icon name="mdi-loading" spin />
                    ) : (
                        <Icon name="mdi-chevron-down" />
                    )}
                </h6>
            </DateRangePicker>
        );
    }

    return (
        <DateRangePicker
            initialSettings={{
                startDate: dateRangeStart || userData?.sessionStartDate || '',
                endDate: dateRangeEnd || userData?.sessionEndDate || '',
                locale: {
                    format: 'DD/MM/YYYY',
                    firstDay: 1,
                    customRangeLabel: i18n.t('date_custom.text'),
                },
                showWeekNumbers: true,
                opens: 'left',
                alwaysShowCalendars: true,
                maxDate: moment().format('DD/MM/YYYY'),
                ranges: {
                    [i18n.t('date_today.text')]: [moment(), moment()],
                    [i18n.t('date_yesterday.text')]: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                    [i18n.t('date_last_7_days.text')]: [moment().subtract(6, 'days'), moment()],
                    [i18n.t('date_last_30_days.text')]: [moment().subtract(29, 'days'), moment()],
                    [i18n.t('date_this_month.text')]: [moment().startOf('month'), moment()],
                    [i18n.t('date_last_month.text')]: [
                        moment().subtract(1, 'month').startOf('month'),
                        moment().subtract(1, 'month').endOf('month')
                    ],
                    [i18n.t('date_this_and_last_month.text')]: [
                        moment().subtract(1, 'month').startOf('month'),
                        moment()
                    ],
                    [i18n.t('date_this_year.text')]: [moment().startOf('year'), moment()]
                },
            }}
            onCallback={(start: Moment, end: Moment, _label) => {
                const startDateStr = start.toDate().toLocaleDateString('en-GB');
                const endDateStr = end.toDate().toLocaleDateString('en-GB');

                setDateRangeStart(startDateStr);
                setDateRangeEnd(endDateStr);
            }}
            onApply={onPickerApply}
        >
            <h4 id="date-range-picker" data-cy="date-range-picker">
                {i18n.t('dates.text')}: {dateRangeStart}-{dateRangeEnd}
                {isSubmitting ? (
                    <Icon name="mdi-loading" spin />
                ) : (
                    <Icon name="mdi-chevron-down" />
                )}
            </h4>
        </DateRangePicker>
    );
};

HeaderDatePickerComponent.displayName = 'HeaderDatePicker';

export const HeaderDatePicker = React.memo(HeaderDatePickerComponent);
export default HeaderDatePicker;
